import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import apiClient from '../utils/apiClient';
import { conversationStyle } from '../utils/conversationStyle';
import { resolveChannel, channelMeta, displayTicketId } from '../utils/channels';
import { XCircleIcon, ChevronUpIcon, ChevronDownIcon, UserIcon, PhoneIcon, TruckIcon, WrenchScrewdriverIcon, MapPinIcon, CalendarIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

// Quote insights links here as /responses?search=<ticket ref> so a row on that
// page opens the conversation behind it — the dashboard used to tell you a
// quote hadn't gone out and give you no way to go and read why. Read ONCE, as
// initial state: after that the box belongs to whoever is typing in it.
function initialSearchFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get('search') || '';
  } catch {
    return '';
  }
}

export default function Responses() {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [threadData, setThreadData] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'sent_at', direction: 'desc' });
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0
  });
  const [filters, setFilters] = useState(() => ({
    responseType: '',
    channel: '',
    fromDate: '',
    toDate: '',
    search: initialSearchFromUrl()
  }));
  // Immediate input value; debounced into filters.search so we don't fire a
  // request on every keystroke.
  const [searchInput, setSearchInput] = useState(initialSearchFromUrl);
  // New state for customer info and response functionality
  const [customerStatus, setCustomerStatus] = useState(null);
  const [loadingCustomerStatus, setLoadingCustomerStatus] = useState(false);
  const [customerHistory, setCustomerHistory] = useState(null);
  const [loadingCustomerHistory, setLoadingCustomerHistory] = useState(false);
  const [showCustomerHistory, setShowCustomerHistory] = useState(false);
  const [showResponseEditor, setShowResponseEditor] = useState(false);
  const [customResponse, setCustomResponse] = useState('');
  const [sendingResponse, setSendingResponse] = useState(false);
  const [overview, setOverview] = useState(null);
  // "New since last visit" marker — compare each thread's latest time to the
  // timestamp we stored on the previous visit (kept in localStorage, per-browser).
  const [lastSeen] = useState(() => localStorage.getItem('responsesLastSeen') || '1970-01-01T00:00:00.000Z');

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedResponses = useMemo(() => {
    const sorted = [...responses];
    sorted.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.key) {
        case 'sent_at':
          aVal = new Date(a.sent_at || 0);
          bVal = new Date(b.sent_at || 0);
          break;
        case 'ticket':
          aVal = a.ticket?.freshdesk_ticket_id || '';
          bVal = b.ticket?.freshdesk_ticket_id || '';
          break;
        case 'customer':
          aVal = (a.ticket?.customer_name || a.ticket?.customer_email || '').toLowerCase();
          bVal = (b.ticket?.customer_name || b.ticket?.customer_email || '').toLowerCase();
          break;
        case 'subject':
          aVal = (a.ticket?.subject || '').toLowerCase();
          bVal = (b.ticket?.subject || '').toLowerCase();
          break;
        case 'intent':
          aVal = (a.ticket?.classification?.intent || '').toLowerCase();
          bVal = (b.ticket?.classification?.intent || '').toLowerCase();
          break;
        case 'type':
          aVal = (a.response_type || '').toLowerCase();
          bVal = (b.response_type || '').toLowerCase();
          break;
        case 'confidence':
          aVal = a.ticket?.classification?.confidence || 0;
          bVal = b.ticket?.classification?.confidence || 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [responses, sortConfig]);

  // Create unified chronological conversation from thread data
  const conversationMessages = useMemo(() => {
    if (!threadData) return [];
    
    console.log('🔍 ThreadData received:', threadData);
    console.log('🔍 Parent ticket conversations:', threadData.parent_ticket?.conversations);
    
    const messages = [];
    
    // Add parent ticket as first customer message.
    //
    // ...unless the transcript already opens with it. For EMAIL the ticket body
    // is the original message and `conversations` holds only the replies, so
    // the parent bubble is the first message. For WhatsApp, Facebook and
    // Messenger the backend builds `conversations` from our OWN tables as the
    // COMPLETE transcript, first message included, while ticket.body is a copy
    // of that same first message — so the customer's words rendered twice, once
    // as "Customer Message" and once as "Customer Reply", identical text and
    // timestamp (seen on a Facebook comment, 9 Sep 2026).
    //
    // Matched on content rather than on channel: it cannot blank a thread whose
    // conversations are empty, and it needs no updating when a channel is added.
    const firstConv = threadData.parent_ticket?.conversations?.[0];
    const parentBody = (threadData.parent_ticket?.body || '').trim();
    const parentIsDuplicated = Boolean(
      parentBody && firstConv && firstConv.incoming !== false
      && (firstConv.body_text || '').trim() === parentBody
    );

    if (threadData.parent_ticket) {
      if (!parentIsDuplicated) {
        messages.push({
          id: `ticket-${threadData.parent_ticket.id}`,
          type: 'customer',
          content: threadData.parent_ticket.body || '[No message content]',
          timestamp: threadData.parent_ticket.created_at,
          subject: threadData.parent_ticket.subject,
          isParent: true,
          // Attachments on the original email (e.g. a proof of payment).
          attachments: threadData.parent_ticket.attachments || [],
        });
      }

      // Add Freshdesk conversations (customer AND consultant replies).
      // sender_role from the backend tags consultant replies sent
      // directly via Freshdesk so they render distinctly instead of
      // being filtered out (previous behaviour).
      if (threadData.parent_ticket.conversations) {
        console.log(`🔍 Processing ${threadData.parent_ticket.conversations.length} conversations`);
        threadData.parent_ticket.conversations.forEach(conv => {
          console.log('🔍 Adding conversation:', conv.id, conv.sender_role, conv.body_text?.substring(0, 50));
          messages.push({
            id: conv.id,
            type: conv.sender_role === 'consultant' ? 'consultant'
                  : (conv.incoming === false ? 'ai' : 'customer'),
            content: conv.body_text || '[No message content]',
            timestamp: conv.created_at,
            isConversation: true,
            // WhatsApp media — fetched through our authed proxy on click.
            mediaType: conv.media_type,
            mediaMsgId: conv.media_url ? conv.id : null,
            // Email (Freshdesk) attachments — pre-signed URLs opened directly.
            attachments: conv.attachments || [],
            // A consultant who typed in Wati -- the provider names them.
            sentByName: conv.sent_by_name || null,
          });
          // WhatsApp: the AI's image reading, shown as a distinct internal note.
          if (conv.ai_note) {
            messages.push({
              id: `${conv.id}-ainote`,
              type: 'note',
              content: conv.ai_note,
              timestamp: conv.created_at,
              responseType: 'image_note',
            });
          }
        });
      }
      
      // Add responses to parent ticket
      if (threadData.parent_ticket.responses) {
        threadData.parent_ticket.responses.forEach(response => {
          messages.push({
            id: `response-${response.id}`,
            type: response.response_type === 'image_note' ? 'note' : 'ai',
            content: response.response_text,
            timestamp: response.sent_at,
            responseType: response.response_type,
            sentByName: response.sent_by_name || null
          });
        });
      }

      // Quote decisions, inline in the thread. A quote that went out and a
      // quote that was blocked are both things a consultant needs to see here
      // rather than in a log: "the chat does not say if a quote was sent, and
      // if not why".
      (threadData.parent_ticket.quote_events || []).forEach((q) => {
        messages.push({
          id: `quote-${q.id}`,
          type: 'quote',
          quoteOutcome: q.outcome,
          content: q.outcome === 'sent'
            ? `Quote sent${q.detail ? ` — ${q.detail}` : ''}`
            : `${q.label}${q.detail && q.detail !== q.label ? ` — ${q.detail}` : ''}`,
          timestamp: q.created_at,
          quoteMeta: q,
        });
      });
    }
    
    // Add follow-up tickets and their responses
    if (threadData.follow_ups) {
      threadData.follow_ups.forEach(followUp => {
        // Add customer follow-up message
        messages.push({
          id: `ticket-${followUp.id}`,
          type: 'customer',
          content: followUp.body || '[No message content]',
          timestamp: followUp.created_at,
          subject: followUp.subject,
          isFollowUp: true
        });
        
        // Add Freshdesk conversations (customer AND consultant) for follow-up.
        if (followUp.conversations) {
          followUp.conversations.forEach(conv => {
            messages.push({
              id: conv.id,
              type: conv.sender_role === 'consultant' ? 'consultant' : 'customer',
              content: conv.body_text || '[No message content]',
              timestamp: conv.created_at,
              isConversation: true
            });
          });
        }
        
        // Add responses to this follow-up
        if (followUp.responses) {
          followUp.responses.forEach(response => {
            messages.push({
              id: `response-${response.id}`,
              type: 'ai',
              content: response.response_text,
              timestamp: response.sent_at,
              responseType: response.response_type,
              sentByName: response.sent_by_name || null
            });
          });
        }
      });
    }
    
    // Sort all messages chronologically
    // Secondary sort: customer messages before AI responses when timestamps are equal
    messages.sort((a, b) => {
      const timeDiff = new Date(a.timestamp) - new Date(b.timestamp);
      if (timeDiff !== 0) return timeDiff;
      
      // If timestamps are equal, customer messages come first
      if (a.type === 'customer' && b.type === 'ai') return -1;
      if (a.type === 'ai' && b.type === 'customer') return 1;
      return 0;
    });
    
    return messages;
  }, [threadData]);

  const SortHeader = ({ label, sortKey, className = '' }) => (
    <th
      className={`px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer bg-gray-50 hover:bg-gray-100 select-none ${className}`}
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        <span>{label}</span>
        <span className="flex flex-col">
          <ChevronUpIcon className={`h-3 w-3 ${sortConfig.key === sortKey && sortConfig.direction === 'asc' ? 'text-primary-600' : 'text-gray-300'}`} />
          <ChevronDownIcon className={`h-3 w-3 -mt-1 ${sortConfig.key === sortKey && sortConfig.direction === 'desc' ? 'text-primary-600' : 'text-gray-300'}`} />
        </span>
      </div>
    </th>
  );

  useEffect(() => {
    fetchResponses();
  }, [pagination.offset, filters]);

  // Debounce the search box (350ms) into the filter that drives the fetch, and
  // reset to the first page when the term changes.
  useEffect(() => {
    const id = setTimeout(() => {
      setFilters(prev => (prev.search === searchInput ? prev : { ...prev, search: searchInput }));
      setPagination(prev => (prev.offset === 0 ? prev : { ...prev, offset: 0 }));
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Auto-refresh: poll every 45s while the tab is visible, and refetch the
  // moment the user returns to the tab — so a thread that arrives while they're
  // away appears without a manual refresh. Silent (no toast/spinner) and paused
  // while a conversation modal or the response editor is open.
  useEffect(() => {
    const REFRESH_MS = 45000;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      if (selectedResponse || showResponseEditor) return;  // don't disrupt
      fetchResponses(true);
    };
    const id = setInterval(tick, REFRESH_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResponse, showResponseEditor, pagination.offset, filters]);

  const fetchResponses = async (silent = false) => {
    const toastId = silent ? null : toast.loading('Loading responses...');
    try {
      if (!silent) setLoading(true);

      // Build query parameters
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset
      });
      
      if (filters.responseType) {
        params.append('response_type', filters.responseType);
      }

      if (filters.channel) {
        params.append('channel', filters.channel);
      }

      if (filters.fromDate) {
        params.append('from_date', filters.fromDate);
      }
      
      if (filters.toDate) {
        params.append('to_date', filters.toDate);
      }

      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }

      // Thread the listing: one row per conversation (email + WhatsApp), so a
      // long chat doesn't flood the page.
      params.append('group', 'true');

      const response = await apiClient.get(`/api/v1/responses/?${params.toString()}`);
      setResponses(response.data.items);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
      if (!silent) {
        // Mark "now" as seen so these threads aren't flagged New on the next
        // visit (the current render still uses the `lastSeen` captured at mount).
        // Silent auto-refreshes do NOT bump it, so threads that arrive while
        // you're away still show the "New" badge.
        localStorage.setItem('responsesLastSeen', new Date().toISOString());
        toast.success('Responses loaded successfully', { id: toastId });
      }
    } catch (error) {
      console.error('Error fetching responses:', error);
      if (!silent) toast.error('Failed to load responses', { id: toastId });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Media lives behind Wati auth, and our proxy needs the JWT header — so fetch
  // it as a blob via apiClient and open the object URL in a new tab.
  const openWhatsAppMedia = async (msgId) => {
    const toastId = toast.loading('Loading media...');
    try {
      const resp = await apiClient.get(`/api/v1/whatsapp/media/${msgId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener');
      toast.dismiss(toastId);
    } catch (error) {
      console.error('Error loading media:', error);
      toast.error('Could not load media', { id: toastId });
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset pagination when filters change
    setPagination(prev => ({
      ...prev,
      offset: 0
    }));
  };

  const handlePageChange = (newOffset) => {
    setPagination(prev => ({
      ...prev,
      offset: newOffset
    }));
  };

  const getResponseTypeColor = (type) => {
    switch (type) {
      case 'automated':
        return 'bg-blue-100 text-blue-800';
      case 'manual':
        return 'bg-green-100 text-green-800';
      case 'edited':
        return 'bg-yellow-100 text-yellow-800';
      case 'task_completed':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Friendly label for a response type (the raw enum is otherwise shown as-is).
  const responseTypeLabel = (type) =>
    type === 'task_completed' ? 'Task done' : type;

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Invalid date';
    }
  };

  const fetchThreadData = async (ticketId) => {
    setLoadingThread(true);
    try {
      const response = await apiClient.get(`/api/v1/threads/${ticketId}`);
      setThreadData(response.data);
    } catch (error) {
      console.error('Error fetching thread:', error);
      toast.error('Failed to load conversation thread');
      setThreadData(null);
    } finally {
      setLoadingThread(false);
    }
  };

  const fetchOverview = async (ticketId) => {
    setOverview(null);
    try {
      const res = await apiClient.get(`/api/v1/tickets/${ticketId}/overview`);
      setOverview(res.data);
    } catch (error) {
      console.error('Error fetching ticket overview:', error);
    }
  };

  const handleViewResponse = async (response) => {
    setSelectedResponse(response);
    if (response.ticket?.id) {
      fetchOverview(response.ticket.id);
      await fetchThreadData(response.ticket.id);
    }
    if (response.ticket?.customer_email) {
      await fetchCustomerStatus(response.ticket.customer_email);
    }
  };

  const handleCloseModal = () => {
    setSelectedResponse(null);
    setThreadData(null);
    setOverview(null);
    setCustomerStatus(null);
    setCustomerHistory(null);
    setShowCustomerHistory(false);
    setShowResponseEditor(false);
    setCustomResponse('');
  };

  const fetchCustomerStatus = async (email) => {
    if (!email) return;
    setLoadingCustomerStatus(true);
    try {
      const response = await apiClient.get(`/api/v1/threads/customer-status/${encodeURIComponent(email)}`);
      setCustomerStatus(response.data);
    } catch (error) {
      console.error('Error fetching customer status:', error);
      setCustomerStatus(null);
    } finally {
      setLoadingCustomerStatus(false);
    }
  };

  const fetchCustomerHistory = async (email) => {
    if (!email) return;
    setLoadingCustomerHistory(true);
    try {
      const response = await apiClient.get(`/api/v1/threads/customer-history/${encodeURIComponent(email)}`);
      setCustomerHistory(response.data);
    } catch (error) {
      console.error('Error fetching customer history:', error);
      toast.error('Failed to load customer history');
      setCustomerHistory(null);
    } finally {
      setLoadingCustomerHistory(false);
    }
  };

  const handleToggleCustomerHistory = async () => {
    if (!showCustomerHistory && !customerHistory && threadData?.parent_ticket?.customer_email) {
      await fetchCustomerHistory(threadData.parent_ticket.customer_email);
    }
    setShowCustomerHistory(!showCustomerHistory);
  };

  const handleSendFollowUpResponse = async () => {
    if (!customResponse.trim()) {
      toast.error('Please enter a response message');
      return;
    }
    
    if (!threadData?.parent_ticket?.id) {
      toast.error('No ticket found to respond to');
      return;
    }

    setSendingResponse(true);
    const toastId = toast.loading('Sending response...');
    
    try {
      await apiClient.post(`/api/v1/responses/send-followup`, {
        ticket_id: threadData.parent_ticket.id,
        response_text: customResponse
      });
      
      toast.success('Response sent successfully', { id: toastId });
      setShowResponseEditor(false);
      setCustomResponse('');
      
      // Refresh thread data to show new response
      if (threadData?.parent_ticket?.id) {
        await fetchThreadData(threadData.parent_ticket.id);
      }
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error('Failed to send response', { id: toastId });
    } finally {
      setSendingResponse(false);
    }
  };

  const getJobStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'accepted':
      case 'paid':
        return 'bg-blue-100 text-blue-800';
      case 'start':
        return 'bg-yellow-100 text-yellow-800';
      case 'quote':
        return 'bg-purple-100 text-purple-800';
      case 'expired':
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && responses.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading responses...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">System Responses</h1>
          <p className="mt-2 text-sm text-gray-600">
            {pagination.total} total responses
          </p>
        </div>
        <button
          onClick={fetchResponses}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div className="md:col-span-3 lg:col-span-4">
            <label htmlFor="search" className="block text-xs font-medium text-gray-700 mb-1.5">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                id="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by customer name, email, phone, or ticket number…"
                className="block w-full pl-3 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <XCircleIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div>
            <label htmlFor="responseType" className="block text-xs font-medium text-gray-700 mb-1.5">
              Response Type
            </label>
            <select
              id="responseType"
              name="responseType"
              value={filters.responseType}
              onChange={handleFilterChange}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="">All Types</option>
              <option value="automated">Automated</option>
              <option value="manual">Manual</option>
              <option value="edited">Edited</option>
            </select>
          </div>
          <div>
            <label htmlFor="channel" className="block text-xs font-medium text-gray-700 mb-1.5">
              Channel
            </label>
            <select
              id="channel"
              name="channel"
              value={filters.channel}
              onChange={handleFilterChange}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              <option value="">All Channels</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="facebook">Facebook</option>
              <option value="messenger">Messenger</option>
            </select>
          </div>
          <div>
            <label htmlFor="fromDate" className="block text-xs font-medium text-gray-700 mb-1.5">
              From Date
            </label>
            <input
              type="date"
              id="fromDate"
              name="fromDate"
              value={filters.fromDate}
              onChange={handleFilterChange}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            />
          </div>
          <div>
            <label htmlFor="toDate" className="block text-xs font-medium text-gray-700 mb-1.5">
              To Date
            </label>
            <input
              type="date"
              id="toDate"
              name="toDate"
              value={filters.toDate}
              onChange={handleFilterChange}
              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
            />
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium text-gray-700 mb-1.5 invisible">Action</label>
            <button
              onClick={() => {
                setSearchInput('');
                setFilters({
                  responseType: '',
                  channel: '',
                  fromDate: '',
                  toDate: '',
                  search: ''
                });
              }}
              className="w-full px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Responses Table — own scroll area so rows always have a scroller,
          with the header pinned. */}
      <div className="bg-white shadow rounded-lg overflow-auto max-h-[70vh]">
        <table className="w-full min-w-[720px] divide-y divide-gray-200">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <SortHeader label="Date" sortKey="sent_at" className="w-[10%]" />
              <SortHeader label="Ticket" sortKey="ticket" className="w-[8%]" />
              <SortHeader label="Customer" sortKey="customer" className="w-[18%]" />
              <SortHeader label="Subject" sortKey="subject" className="w-[22%]" />
              <SortHeader label="Intent" sortKey="intent" className="w-[14%]" />
              <SortHeader label="Type" sortKey="type" className="w-[10%]" />
              <SortHeader label="Conf" sortKey="confidence" className="w-[8%]" />
              <th className="w-[10%] px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedResponses.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-2 py-12 text-center text-gray-500">
                  No responses found
                </td>
              </tr>
            ) : (
              sortedResponses.map((response) => (
                <tr key={response.id} className="hover:bg-gray-50">
                  <td className="px-2 py-3 text-xs text-gray-500">
                    <div className="whitespace-nowrap">
                      {new Date(response.sent_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} {new Date(response.sent_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                    {response.sent_at && new Date(response.sent_at) > new Date(lastSeen) && (
                      <span className="inline-flex items-center mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700" title="New reply since your last visit">
                        New
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-3 text-xs text-gray-500">
                    {(() => {
                      // Grouped rows carry a `channels` array; fall back to the
                      // single channel for flat rows.
                      const channels = (response.channels && response.channels.length)
                        ? response.channels
                        : [resolveChannel(response.ticket)];
                      return (
                        <div className="flex flex-col gap-0.5">
                          <div className="flex flex-wrap gap-0.5">
                            {channels.map((ch) => {
                              const meta = channelMeta(ch);
                              return (
                                <span
                                  key={ch}
                                  className={`inline-flex items-center w-fit px-1.5 py-0.5 rounded text-[10px] font-semibold ${meta.classes}`}
                                  title={meta.label}
                                >
                                  {meta.label}
                                </span>
                              );
                            })}
                          </div>
                          <span className="truncate" title={response.ticket?.freshdesk_ticket_id}>
                            #{displayTicketId(response.ticket?.freshdesk_ticket_id) || 'N/A'}
                          </span>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-3">
                    <div className="text-xs font-medium text-gray-900 truncate" title={response.ticket?.customer_name || 'Unknown'}>
                      {response.ticket?.customer_name || 'Unknown'}
                    </div>
                    <div className="text-xs text-gray-400 truncate" title={response.ticket?.customer_email}>
                      {response.ticket?.customer_email || 'N/A'}
                    </div>
                  </td>
                  <td className="px-2 py-3">
                    <div className="text-xs text-gray-900 truncate" title={response.ticket?.subject}>
                      {response.ticket?.subject || 'N/A'}
                    </div>
                    {response.count > 1 && (
                      <div className="text-[10px] text-gray-400 mt-0.5">{response.count} messages</div>
                    )}
                  </td>
                  <td className="px-2 py-3">
                    <span className="text-xs font-medium text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">
                      {response.ticket?.classification?.intent?.replace('_', ' ') || 'N/A'}
                    </span>
                  </td>
                  <td className="px-2 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span
                        className={`px-1.5 py-0.5 inline-flex w-fit text-xs leading-4 font-semibold rounded-full ${getResponseTypeColor(
                          response.response_type
                        )}`}
                      >
                        {responseTypeLabel(response.response_type)}
                      </span>
                      {response.automated === true && (
                        <span className="inline-flex items-center w-fit px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800" title="Sent automatically by Henry (no human review)">
                          Auto
                        </span>
                      )}
                      {response.automated === false && (
                        <span className="inline-flex items-center w-fit px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800" title="Approved/sent by a human">
                          Manual
                        </span>
                      )}
                      {(response.automated === null || response.automated === undefined) && (
                        <span className="inline-flex items-center w-fit px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600" title="System action (e.g. quote or payment link)">
                          System
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-3 text-sm text-gray-900">
                    {response.ticket?.classification?.confidence
                      ? `${response.ticket.classification.confidence.toFixed(0)}%`
                      : 'N/A'}
                  </td>
                  <td className="px-2 py-3 text-sm font-medium">
                    <button
                      onClick={() => handleViewResponse(response)}
                      className="text-primary-600 hover:text-primary-900"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
            disabled={pagination.offset === 0}
            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
              pagination.offset === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Previous
          </button>
          <button
            onClick={() =>
              handlePageChange(
                pagination.offset + pagination.limit < pagination.total
                  ? pagination.offset + pagination.limit
                  : pagination.offset
              )
            }
            disabled={pagination.offset + pagination.limit >= pagination.total}
            className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
              pagination.offset + pagination.limit >= pagination.total
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{pagination.offset + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(pagination.offset + pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-medium">{pagination.total}</span> results
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => handlePageChange(0)}
                disabled={pagination.offset === 0}
                className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                  pagination.offset === 0
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">First</span>
                <span>First</span>
              </button>
              <button
                onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                disabled={pagination.offset === 0}
                className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                  pagination.offset === 0
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Previous</span>
                <span>Prev</span>
              </button>
              <button
                onClick={() =>
                  handlePageChange(
                    pagination.offset + pagination.limit < pagination.total
                      ? pagination.offset + pagination.limit
                      : pagination.offset
                  )
                }
                disabled={pagination.offset + pagination.limit >= pagination.total}
                className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                  pagination.offset + pagination.limit >= pagination.total
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Next</span>
                <span>Next</span>
              </button>
              <button
                onClick={() =>
                  handlePageChange(
                    Math.max(0, Math.floor(pagination.total / pagination.limit) * pagination.limit)
                  )
                }
                disabled={pagination.offset + pagination.limit >= pagination.total}
                className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                  pagination.offset + pagination.limit >= pagination.total
                    ? 'text-gray-300 cursor-not-allowed'
                    : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                <span className="sr-only">Last</span>
                <span>Last</span>
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Response Detail Modal */}
      {selectedResponse && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            {/* Fixed Header */}
            <div className="p-6 border-b border-gray-200 shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Ticket Conversation</h2>
                  {threadData && (
                    <p className="text-sm text-gray-500 mt-1">
                      {conversationMessages.length} message{conversationMessages.length !== 1 ? 's' : ''} in this thread
                    </p>
                  )}
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 overflow-y-auto flex-1 min-h-0">
              {/* At-a-glance overview: summary + what's transpired */}
              {overview && (
                <div className="mb-6 bg-indigo-50 border border-indigo-100 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-indigo-900 uppercase tracking-wide mb-1">Summary</h3>
                  <p className="text-sm text-gray-800">{overview.summary}</p>
                  {overview.activity?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-indigo-100">
                      <h4 className="text-xs font-semibold text-indigo-900 uppercase tracking-wide mb-2">Activity</h4>
                      <ul className="space-y-1">
                        {overview.activity.map((a, idx) => (
                          <li key={idx} className="flex items-start justify-between text-xs gap-3">
                            <span className="text-gray-700">
                              <span className="font-medium">{a.label}</span>
                              {a.detail ? <span className="text-gray-500"> — {a.detail}</span> : null}
                            </span>
                            <span className="text-gray-400 whitespace-nowrap">
                              {a.at ? new Date(a.at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {loadingThread ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-500">Loading conversation...</div>
                </div>
              ) : threadData ? (
                <div className="space-y-6">
                  {/* Ticket Info with Customer Status */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Ticket</h3>
                        <p className="mt-1 text-sm text-gray-900">
                          {threadData.parent_ticket.subject || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500">
                          ID: {threadData.parent_ticket.freshdesk_ticket_id}
                        </p>
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-500">Customer</h3>
                        <div className="mt-1 flex items-center gap-2">
                          <span className="text-sm text-gray-900">
                            {threadData.parent_ticket.customer_name || 'Unknown'}
                          </span>
                          {loadingCustomerStatus ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">
                              Checking...
                            </span>
                          ) : customerStatus?.system_address ? (
                            <span
                              className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800"
                              title="The requester address is Michanic's own forms mailbox — the sender's identity comes from the form (name/phone), not this email"
                            >
                              Web form — our own address, identity via form details
                            </span>
                          ) : customerStatus?.is_existing_customer ? (
                            <button
                              onClick={handleToggleCustomerHistory}
                              className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 hover:bg-green-200 cursor-pointer flex items-center gap-1"
                            >
                              <UserIcon className="h-3 w-3" />
                              Existing Customer
                              {customerStatus.job_count > 0 && (
                                <span className="ml-1">({customerStatus.job_count} jobs)</span>
                              )}
                            </button>
                          ) : customerStatus ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              New Customer
                            </span>
                          ) : null}
                        </div>
                        <p className="text-xs text-gray-500">
                          {threadData.parent_ticket.customer_email}
                        </p>
                      </div>
                    </div>

                    {/* Customer History Panel - Expandable */}
                    {showCustomerHistory && customerStatus?.is_existing_customer && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <UserIcon className="h-4 w-4 text-green-600" />
                            Customer History from Brain CRM
                          </h3>
                          <button
                            onClick={() => setShowCustomerHistory(false)}
                            className="text-xs text-gray-500 hover:text-gray-700"
                          >
                            Hide
                          </button>
                        </div>
                        
                        {loadingCustomerHistory ? (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            Loading customer history...
                          </div>
                        ) : customerHistory?.found ? (
                          <div className="bg-green-50 rounded-lg p-4 space-y-4">
                            {/* Customer Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              <div className="bg-white rounded-lg p-3 shadow-sm">
                                <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                  <UserIcon className="h-3 w-3" />
                                  Name
                                </div>
                                <p className="text-sm font-medium text-gray-900">
                                  {customerHistory.customer_name || 'N/A'}
                                </p>
                              </div>
                              <div className="bg-white rounded-lg p-3 shadow-sm">
                                <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                  <PhoneIcon className="h-3 w-3" />
                                  Phone
                                </div>
                                <p className="text-sm font-medium text-gray-900">
                                  {customerHistory.phone || 'N/A'}
                                </p>
                              </div>
                              <div className="bg-white rounded-lg p-3 shadow-sm">
                                <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                  <WrenchScrewdriverIcon className="h-3 w-3" />
                                  Total Jobs
                                </div>
                                <p className="text-sm font-medium text-gray-900">
                                  {customerHistory.summary?.total_jobs || 0}
                                </p>
                              </div>
                              <div className="bg-white rounded-lg p-3 shadow-sm">
                                <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                                  <TruckIcon className="h-3 w-3" />
                                  Vehicles
                                </div>
                                <p className="text-sm font-medium text-gray-900">
                                  {customerHistory.summary?.unique_vehicles || 0}
                                </p>
                              </div>
                            </div>

                            {/* Vehicles List */}
                            {customerHistory.summary?.vehicles?.length > 0 && (
                              <div>
                                <h4 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                                  <TruckIcon className="h-3 w-3" />
                                  Registered Vehicles
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                  {customerHistory.summary.vehicles.map((vehicle, idx) => (
                                    <span key={idx} className="px-2 py-1 bg-white rounded text-xs text-gray-700 shadow-sm">
                                      {vehicle}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Job History */}
                            {customerHistory.jobs?.length > 0 && (
                              <div>
                                <h4 className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                                  <CalendarIcon className="h-3 w-3" />
                                  Job History
                                </h4>
                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                  {customerHistory.jobs.map((job, idx) => (
                                    <div key={idx} className="bg-white rounded-lg p-3 shadow-sm">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-xs font-medium text-gray-900">
                                              Job #{job.job_number}
                                            </span>
                                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getJobStatusColor(job.status)}`}>
                                              {job.status}
                                            </span>
                                          </div>
                                          {job.vehicle?.display && (
                                            <p className="text-xs text-gray-600 flex items-center gap-1">
                                              <TruckIcon className="h-3 w-3" />
                                              {job.vehicle.display}
                                            </p>
                                          )}
                                          {job.location && (
                                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                              <MapPinIcon className="h-3 w-3" />
                                              {job.location}
                                            </p>
                                          )}
                                        </div>
                                        <div className="text-right text-xs text-gray-500">
                                          {job.booked_date && (
                                            <p>Booked: {job.booked_date}</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-gray-500 text-sm bg-gray-50 rounded-lg">
                            {customerHistory?.error ? `Error: ${customerHistory.error}` : 'No history found in CRM'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Conversation Thread - Unified Chronological View */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Conversation History
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({conversationMessages.length} message{conversationMessages.length !== 1 ? 's' : ''})
                      </span>
                    </h3>
                    
                    {conversationMessages.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No messages in this conversation
                      </div>
                    ) : (
                      conversationMessages.map((message, index) => {
                        const cs = conversationStyle(message);
                        return (
                        <div
                          key={message.id}
                          className={`border-l-4 ${cs.border} pl-4 py-2`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <span className={`text-xs font-semibold uppercase ${cs.text}`}>
                                {cs.heading}
                              </span>
                              {cs.badge && (
                                <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-slate-200 text-slate-700 uppercase">
                                  {cs.badge}
                                </span>
                              )}
                              {cs.kind === 'ai' && message.responseType && (
                                <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getResponseTypeColor(message.responseType)}`}>
                                  {message.responseType}
                                </span>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDate(message.timestamp)}
                                {message.sentByName && (
                                  <>
                                    {' \u00b7 '}
                                    <span className="font-medium text-gray-700">
                                      {message.sentByName}
                                    </span>
                                  </>
                                )}
                              </p>
                            </div>
                            <span className="text-xs text-gray-400">#{index + 1}</span>
                          </div>
                          <div className={`rounded-lg p-4 mt-2 ${cs.bubble}`}>
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">
                              {message.content}
                            </p>
                            {message.mediaMsgId && (
                              <button
                                onClick={() => openWhatsAppMedia(message.mediaMsgId)}
                                className="mt-2 inline-flex items-center text-xs font-medium text-primary-600 hover:text-primary-800 underline"
                              >
                                View {message.mediaType || 'media'}
                              </button>
                            )}
                            {message.attachments && message.attachments.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {message.attachments.map((att, ai) => (
                                  (att.content_type || '').startsWith('image/') ? (
                                    <a key={ai} href={att.url} target="_blank" rel="noopener noreferrer" title={att.name}>
                                      <img src={att.url} alt={att.name}
                                           className="h-20 w-20 object-cover rounded border border-gray-200 hover:opacity-90" />
                                    </a>
                                  ) : (
                                    <a key={ai} href={att.url} target="_blank" rel="noopener noreferrer"
                                       className="inline-flex items-center text-xs font-medium text-primary-600 hover:text-primary-800 underline">
                                      📎 {att.name}
                                    </a>
                                  )
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })
                    )}
                  </div>

                  {/* Response Editor */}
                  {showResponseEditor && (
                    <div className="mt-4 border-t pt-4">
                      <p className="text-sm text-gray-600 mb-2">Type your follow-up response below:</p>
                      <textarea
                        value={customResponse}
                        onChange={(e) => setCustomResponse(e.target.value)}
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Type your response here..."
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No conversation data available
                </div>
              )}
            </div>

            {/* Fixed Footer with Actions */}
            {threadData && (
              <div className="p-4 border-t border-gray-200 bg-gray-50 flex flex-wrap justify-end gap-3 shrink-0">
                {showResponseEditor ? (
                  <>
                    <button
                      onClick={() => {
                        setShowResponseEditor(false);
                        setCustomResponse('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendFollowUpResponse}
                      disabled={sendingResponse || !customResponse.trim()}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center"
                    >
                      <CheckCircleIcon className="h-5 w-5 mr-2" />
                      {sendingResponse ? 'Sending...' : 'Send Response'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowResponseEditor(true)}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    Send Follow-up Response
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
